/* eslint-disable max-lines */
export type RCAStep = {
  id: string;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  description: string;
  findings?: string;
  timestamp: string;
  duration?: number; // in seconds
};

export type UserFeedback = {
  id: string;
  userId: string;
  userName: string;
  organizationId: string;
  investigationId: string;
  rating: 'positive' | 'negative';
  comment?: string;
  source: 'slack' | 'ui';
  createdAt: string;
  updatedAt: string;
};

export type RCAAnalysis = {
  id: string;
  incidentId: string;
  incidentNumber: number;
  status: 'investigating' | 'completed' | 'failed' | 'timeout';
  rootCause?: string;
  confidence: number; // 0-100
  steps: RCAStep[];
  startedAt: string;
  completedAt?: string;
  totalDuration?: number; // in seconds
  userFeedback?: UserFeedback[];
};

export const mockRCAData: RCAAnalysis[] = [
  {
    id: 'rca-001',
    incidentId: 'inc-12345',
    incidentNumber: 1001,
    status: 'completed',
    rootCause:
      'Database connection pool exhaustion due to inefficient query in user service causing connection timeout cascade',
    confidence: 92,
    startedAt: '2024-12-15T10:30:00Z',
    completedAt: '2024-12-15T10:35:30Z',
    totalDuration: 330,
    userFeedback: [
      {
        id: 'feedback-001',
        userId: 'user-001',
        userName: 'Alice Johnson',
        organizationId: 'org-001',
        investigationId: 'rca-001',
        rating: 'positive',
        comment:
          'Great analysis! The root cause identification was spot on and helped us fix the issue quickly.',
        source: 'ui',
        createdAt: '2024-12-15T11:00:00Z',
        updatedAt: '2024-12-15T11:00:00Z',
      },
      {
        id: 'feedback-002',
        userId: 'user-002',
        userName: 'Bob Smith',
        organizationId: 'org-001',
        investigationId: 'rca-001',
        rating: 'positive',
        comment: 'Annie did an excellent job tracing the deployment timeline.',
        source: 'slack',
        createdAt: '2024-12-15T11:15:00Z',
        updatedAt: '2024-12-15T11:15:00Z',
      },
    ],
    steps: [
      {
        id: 'step-001',
        title: 'Acknowledge incident',
        status: 'completed',
        description: 'Annie acknowledged the incident and began investigation',
        findings:
          'PagerDuty alert triggered for high error rate in user authentication service',
        timestamp: '2024-12-15T10:30:00Z',
        duration: 5,
      },
      {
        id: 'step-002',
        title: 'Analyze service metrics',
        status: 'completed',
        description: 'Examining service performance metrics and error patterns',
        findings:
          'User service showing 95% error rate, database connection timeout errors increasing',
        timestamp: '2024-12-15T10:30:30Z',
        duration: 45,
      },
      {
        id: 'step-003',
        title: 'Check database health',
        status: 'completed',
        description:
          'Investigating database performance and connection pool status',
        findings:
          'Database connection pool at 100% utilization, query execution time >30s for user lookup queries',
        timestamp: '2024-12-15T10:31:15Z',
        duration: 60,
      },
      {
        id: 'step-004',
        title: 'Identify problematic queries',
        status: 'completed',
        description: 'Analyzing slow queries and database locks',
        findings:
          'SELECT query on users table missing index on email column, causing table scan on 10M+ records',
        timestamp: '2024-12-15T10:32:15Z',
        duration: 90,
      },
      {
        id: 'step-005',
        title: 'Trace incident timeline',
        status: 'completed',
        description: 'Correlating deployment timeline with incident onset',
        findings:
          'New user service deployment 2 hours ago introduced unoptimized query for email validation',
        timestamp: '2024-12-15T10:33:45Z',
        duration: 130,
      },
    ],
  },
  {
    id: 'rca-002',
    incidentId: 'inc-12346',
    incidentNumber: 1002,
    status: 'investigating',
    confidence: 0,
    startedAt: '2024-12-15T11:15:00Z',
    steps: [
      {
        id: 'step-006',
        title: 'Acknowledge incident',
        status: 'completed',
        description: 'Annie acknowledged the incident and began investigation',
        findings: 'API gateway showing intermittent 502 errors',
        timestamp: '2024-12-15T11:15:00Z',
        duration: 5,
      },
      {
        id: 'step-007',
        title: 'Check load balancer status',
        status: 'completed',
        description: 'Investigating load balancer health and upstream services',
        findings:
          'Load balancer health checks failing for 2 out of 5 backend instances',
        timestamp: '2024-12-15T11:15:30Z',
        duration: 30,
      },
      {
        id: 'step-008',
        title: 'Analyze failing instances',
        status: 'in_progress',
        description:
          'Examining logs and metrics for unhealthy backend instances',
        timestamp: '2024-12-15T11:16:00Z',
      },
      {
        id: 'step-009',
        title: 'Check resource utilization',
        status: 'pending',
        description:
          'Investigating CPU, memory, and disk usage on affected instances',
        timestamp: '2024-12-15T11:16:30Z',
      },
    ],
  },
  {
    id: 'rca-003',
    incidentId: 'inc-12347',
    incidentNumber: 1003,
    status: 'failed',
    confidence: 25,
    startedAt: '2024-12-15T09:45:00Z',
    completedAt: '2024-12-15T09:50:00Z',
    totalDuration: 300,
    userFeedback: [
      {
        id: 'feedback-003',
        userId: 'user-003',
        userName: 'Charlie Brown',
        organizationId: 'org-001',
        investigationId: 'rca-003',
        rating: 'negative',
        comment:
          'The investigation failed due to permission issues. Need better access controls.',
        source: 'ui',
        createdAt: '2024-12-15T10:00:00Z',
        updatedAt: '2024-12-15T10:00:00Z',
      },
    ],
    steps: [
      {
        id: 'step-010',
        title: 'Acknowledge incident',
        status: 'completed',
        description: 'Annie acknowledged the incident and began investigation',
        findings:
          'Network connectivity issues reported across multiple services',
        timestamp: '2024-12-15T09:45:00Z',
        duration: 5,
      },
      {
        id: 'step-011',
        title: 'Check network infrastructure',
        status: 'failed',
        description: 'Attempting to analyze network routing and connectivity',
        findings:
          'Unable to access network monitoring tools - insufficient permissions',
        timestamp: '2024-12-15T09:45:30Z',
        duration: 60,
      },
      {
        id: 'step-012',
        title: 'Analyze service dependencies',
        status: 'failed',
        description: 'Investigating service-to-service communication patterns',
        findings:
          'Limited visibility into network layer - escalating to network team',
        timestamp: '2024-12-15T09:46:30Z',
        duration: 45,
      },
    ],
  },
  {
    id: 'rca-004',
    incidentId: 'inc-12348',
    incidentNumber: 1004,
    status: 'investigating',
    confidence: 0,
    startedAt: '2024-12-15T14:20:00Z',
    steps: [
      {
        id: 'step-013',
        title: 'Acknowledge incident',
        status: 'completed',
        description: 'Annie acknowledged the critical database incident',
        findings:
          'Critical alert: Primary database cluster down, affecting all services',
        timestamp: '2024-12-15T14:20:00Z',
        duration: 3,
      },
      {
        id: 'step-014',
        title: 'Check database cluster status',
        status: 'completed',
        description: 'Investigating primary database cluster health',
        findings:
          'Primary cluster unreachable, secondary cluster under high load',
        timestamp: '2024-12-15T14:20:30Z',
        duration: 15,
      },
      {
        id: 'step-015',
        title: 'Analyze failover mechanisms',
        status: 'completed',
        description: 'Examining automatic failover and backup procedures',
        findings:
          'Auto-failover disabled for maintenance, manual intervention required',
        timestamp: '2024-12-15T14:21:00Z',
        duration: 20,
      },
      {
        id: 'step-016',
        title: 'Coordinate emergency response',
        status: 'in_progress',
        description: 'Coordinating with database team for manual failover',
        timestamp: '2024-12-15T14:22:00Z',
      },
    ],
  },
  {
    id: 'rca-005',
    incidentId: 'inc-12349',
    incidentNumber: 1005,
    status: 'investigating',
    confidence: 0,
    startedAt: '2024-12-15T16:10:00Z',
    steps: [
      {
        id: 'step-017',
        title: 'Acknowledge performance reports',
        status: 'completed',
        description: 'Annie acknowledged user-reported performance issues',
        findings:
          'Multiple user reports of slow response times in support channel',
        timestamp: '2024-12-15T16:10:00Z',
        duration: 2,
      },
      {
        id: 'step-018',
        title: 'Analyze response times',
        status: 'completed',
        description: 'Examining application performance metrics',
        findings:
          '25% increase in average response time, primarily affecting web frontend',
        timestamp: '2024-12-15T16:11:00Z',
        duration: 30,
      },
      {
        id: 'step-019',
        title: 'Check CDN performance',
        status: 'in_progress',
        description: 'Investigating CDN edge server performance',
        timestamp: '2024-12-15T16:12:00Z',
      },
    ],
  },
];

export const getRCAByIncidentId = (
  incidentId: string,
): RCAAnalysis | undefined => {
  return mockRCAData.find(rca => rca.incidentId === incidentId);
};

export const getRCAByIncidentNumber = (
  incidentNumber: number,
): RCAAnalysis | undefined => {
  return mockRCAData.find(rca => rca.incidentNumber === incidentNumber);
};

export const getRCAByDemoIncidentId = (
  demoIncidentId: string,
): RCAAnalysis | undefined => {
  // Extract the RCA ID from the demo incident ID (e.g., "inc-demo-rca-001" -> "rca-001")
  const rcaId = demoIncidentId.replace('inc-demo-', '');

  return mockRCAData.find(rca => rca.id === rcaId);
};

const getDemoIncidentTitle = (index: number): string => {
  switch (index) {
    case 0:
      return 'Database Connection Pool Exhausted';
    case 1:
      return 'High CPU Usage - Memory Leak';
    default:
      return 'Service Timeout Issues';
  }
};

const getDemoPriority = (index: number): string => {
  switch (index) {
    case 0:
      return 'P1';
    case 1:
      return 'P2';
    default:
      return 'P3';
  }
};

// Mock incidents for the demo based on RCA data
export const mockDemoIncidents = mockRCAData.map((rca, index) => ({
  id: `inc-demo-${rca.id}`,
  createdAt: rca.startedAt,
  updatedAt: rca.completedAt ?? rca.startedAt,
  projectId: 'demo-project-rocket-banana-labs',
  pagerDutyIncident: {
    id: `pd-${rca.id}`,
    incidentId: `inc-demo-${rca.id}`,
    createdAt: rca.startedAt,
    updatedAt: rca.completedAt ?? rca.startedAt,
    projectId: 'demo-project-rocket-banana-labs',
    pagerDutyId: `PD${rca.incidentNumber.toString().padStart(6, '0')}`,
    title: getDemoIncidentTitle(index),
    summary:
      rca.rootCause ?? 'Investigating system issues affecting user experience',
    incidentNumber: rca.incidentNumber,
    status: rca.status === 'completed' ? 'resolved' : 'triggered',
    startedAt: rca.startedAt,
    resolvedAt: rca.status === 'completed' ? rca.completedAt : undefined,
    priority: getDemoPriority(index),
    url: `https://demo.pagerduty.com/incidents/PD${rca.incidentNumber.toString().padStart(6, '0')}`,
  },
  sources: [{ type: 'pagerduty' as const, name: 'PagerDuty' }],
}));
